import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Textbox from 'carbon-react/lib/components/textbox';

import { ReportTemplateList } from '@futrli-sdk/predict';
import { getReportingEditTemplateUrl } from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportTemplateView } from '@futrli/views/views/reports/ModifyReportTemplateView/ModifyReportTemplateView';

import { useRouteIds } from '../../../hooks/useRouteIds';

export interface DuplicateTemplateWizardContainerProps {
  onClose: () => void;
  template: ReportTemplateList;
}

type Screen = 'form' | 'duplicating' | 'duplicated';

const FIELD_ID = 'duplicate-template-name';

export const DuplicateTemplateWizardContainer = ({ onClose, template }: DuplicateTemplateWizardContainerProps) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ModifyReportTemplateView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const copyLabel = intl.formatMessage({ id: 'common.copy', defaultMessage: 'Copy' });
  const [templateName, setTemplateName] = useState(`[${copyLabel}] ${template.name}`);
  const [error, setError] = useState<string | undefined>();
  const [validationErrors, setValidationErrors] = useState<Record<string, { id: string; message: string }>>({});
  const hasBeenValidated = useRef(false);

  const validate = useCallback(() => {
    const errors: Record<string, { id: string; message: string }> = {};

    if (!templateName.trim()) {
      errors[FIELD_ID] = {
        id: FIELD_ID,
        message: intl.formatMessage({
          id: 'reporting.validation.template_name_is_required',
          defaultMessage: 'Template name is required'
        })
      };
    }

    setValidationErrors(errors);
    hasBeenValidated.current = true;
    return Object.keys(errors).length === 0;
  }, [templateName, intl]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  useEffect(() => {
    if (view.state.isDuplicatingTemplate) {
      setScreen('duplicating');
    }
  }, [view.state.isDuplicatingTemplate]);

  useEffect(() => {
    if (view.state.duplicatedTemplateId) {
      setScreen('duplicated');
    }
  }, [view.state.duplicatedTemplateId]);

  useEffect(() => {
    if (view.state.errorDuplicatingTemplate) {
      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('form');
    }
  }, [view.state.errorDuplicatingTemplate, intl]);

  const handleSave = useCallback(() => {
    if (!validate()) {
      return;
    }

    setError(undefined);
    view.duplicateTemplate({
      templateId: template.id,
      templateName: templateName.trim()
    });
  }, [validate, view, template.id, templateName]);

  const handleView = useCallback(() => {
    if (view.state.duplicatedTemplateId) {
      onClose();
      navigate(
        getReportingEditTemplateUrl(workspaceId, organisationId, view.state.duplicatedTemplateId, isConsolidation)
      );
    }
  }, [view.state.duplicatedTemplateId, navigate, workspaceId, organisationId, isConsolidation, onClose]);

  if (screen === 'duplicating') {
    return (
      <LoadingWizardScreen
        text={intl.formatMessage({ id: 'reporting.creating_template', defaultMessage: 'Creating template...' })}
      />
    );
  }

  if (screen === 'duplicated') {
    return (
      <WizardScreen
        footerMode={FooterMode.CREATED}
        onCancel={onClose}
        onView={handleView}
        viewText={intl.formatMessage({ id: 'reporting.go_to_template', defaultMessage: 'Go to template' })}
      >
        <WizardFinished
          text={intl.formatMessage({
            id: 'reporting.template_created_and_saved_as_draft',
            defaultMessage: 'Report template created and saved as a draft for review'
          })}
        />
      </WizardScreen>
    );
  }

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={handleSave}
      createText={intl.formatMessage({ id: 'common.save', defaultMessage: 'Save' })}
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={3}>
          <Textbox
            label={intl.formatMessage({ id: 'reporting.template_name', defaultMessage: 'Template name' })}
            name={FIELD_ID}
            id={FIELD_ID}
            value={templateName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateName(e.target.value)}
            onBlur={revalidate}
            error={validationErrors[FIELD_ID]?.message}
            required
          />
        </Box>
      </>
    </WizardScreen>
  );
};
